/**
 * Futuristic Layout Types for UniãoBR Platform
 * 
 * This file defines all layout types, categories, and configuration interfaces
 * for the 15 futuristic layouts plus 4 basic layouts.
 */

// Basic layouts (existing)
export type BasicLayoutType = 'SIDEBAR' | 'TOPBAR' | 'MINIMAL' | 'DASHBOARD_FIRST';

// Futuristic layouts - Navigation Innovation
export type NavigationLayoutType = 'BUBBLE_NAVIGATION' | 'ORBITAL' | 'HEXAGONAL_GRID';

// Futuristic layouts - Visual Effects
export type VisualEffectsLayoutType = 'GLASS_MORPHISM' | 'NEON_CYBERPUNK' | 'AURORA_BOREALIS';

// Futuristic layouts - 3D and Depth
export type ThreeDLayoutType = 'ISOMETRIC_3D' | 'PARALLAX_LAYERS' | 'FLOATING_CARDS';

// Futuristic layouts - Animations and Particles
export type AnimationsLayoutType = 'PARTICLE_FLOW' | 'LIQUID_MOTION' | 'MATRIX_RAIN';

// Futuristic layouts - Sci-Fi
export type SciFiLayoutType = 'HOLOGRAPHIC' | 'COMMAND_CENTER' | 'NEURAL_NETWORK';

// All futuristic layouts combined
export type FuturisticOnlyLayoutType =
  | NavigationLayoutType
  | VisualEffectsLayoutType
  | ThreeDLayoutType
  | AnimationsLayoutType
  | SciFiLayoutType;

// Complete layout type (basic + futuristic)
export type FuturisticLayoutType = BasicLayoutType | FuturisticOnlyLayoutType;

// Layout categories
export type LayoutCategory =
  | 'BASIC'
  | 'NAVIGATION'
  | 'VISUAL_EFFECTS'
  | '3D_DEPTH'
  | 'ANIMATIONS'
  | 'SCIFI';

// Animation speed options
export type AnimationSpeed = 'slow' | 'normal' | 'fast';

// Bubble arrangement options
export type BubbleArrangement = 'circular' | 'linear' | 'clustered';

// Blur intensity options
export type BlurIntensity = 'light' | 'medium' | 'heavy';

// Neon color options
export type NeonColor = 'primary' | 'accent' | 'cyan' | 'magenta' | 'yellow';

// Layout-specific settings interfaces
export interface BubbleNavigationSettings {
  arrangement: BubbleArrangement;
  bubbleSize: number;
  showLabelsOnHover: boolean;
}

export interface OrbitalSettings {
  orbitRadius: number;
  orbitSpeed: number;
  pauseOnFocus: boolean;
}

export interface HexagonalGridSettings {
  hexagonSize: number;
  gap: number;
  columns: number;
}

export interface GlassMorphismSettings {
  blurIntensity: BlurIntensity;
  transparency: number;
  borderGlow: boolean;
}

export interface NeonCyberpunkSettings {
  primaryNeonColor: NeonColor;
  secondaryNeonColor: NeonColor;
  neonIntensity: 'low' | 'medium' | 'high';
  flickerEnabled: boolean;
}

export interface AuroraBorealisSettings {
  colors: string[];
  waveSpeed: AnimationSpeed;
}

export interface Isometric3DSettings {
  perspectiveAngle: number;
  depth: number;
  tiltOnHover: boolean;
}

export interface ParallaxLayersSettings {
  layerCount: number;
  parallaxIntensity: number;
}

export interface FloatingCardsSettings {
  elevationLevel: number;
  shadowColor: string;
  floatAnimation: boolean;
}

export interface ParticleFlowSettings {
  particleCount: number;
  connectionDistance: number;
  particleColor: string;
  lineColor: string;
  interactive: boolean;
}

export interface LiquidMotionSettings {
  blobCount: number;
  blobColors: string[];
  morphSpeed: AnimationSpeed;
}

export interface MatrixRainSettings {
  density: number;
  speed: number;
  characters: string;
  rainColor: string;
}

export interface HolographicSettings {
  colorPalette: string[];
  shimmerSpeed: AnimationSpeed;
  mouseInteraction: boolean;
}

export interface CommandCenterSettings {
  scanLinesEnabled: boolean;
  gridOverlay: boolean;
  accentColor: string;
  monospaceFont: boolean;
}

export interface NeuralNetworkSettings {
  nodeCount: number;
  connectionStyle: 'straight' | 'curved';
  pulseAnimation: boolean;
  dataFlowSpeed: AnimationSpeed;
}

// Union type for all layout-specific settings
export type LayoutSpecificSettings =
  | BubbleNavigationSettings
  | OrbitalSettings
  | HexagonalGridSettings
  | GlassMorphismSettings
  | NeonCyberpunkSettings
  | AuroraBorealisSettings
  | Isometric3DSettings
  | ParallaxLayersSettings
  | FloatingCardsSettings
  | ParticleFlowSettings
  | LiquidMotionSettings
  | MatrixRainSettings
  | HolographicSettings
  | CommandCenterSettings
  | NeuralNetworkSettings;

// Main layout settings interface
export interface LayoutSettings {
  // Common settings
  animationSpeed: AnimationSpeed;
  reducedMotion: boolean;
  darkMode: boolean;
  
  // Layout-specific settings (optional, depends on layout type)
  bubbleNavigation?: BubbleNavigationSettings;
  orbital?: OrbitalSettings;
  hexagonalGrid?: HexagonalGridSettings;
  glassMorphism?: GlassMorphismSettings;
  neonCyberpunk?: NeonCyberpunkSettings;
  auroraBorealis?: AuroraBorealisSettings;
  isometric3D?: Isometric3DSettings;
  parallaxLayers?: ParallaxLayersSettings;
  floatingCards?: FloatingCardsSettings;
  particleFlow?: ParticleFlowSettings;
  liquidMotion?: LiquidMotionSettings;
  matrixRain?: MatrixRainSettings;
  holographic?: HolographicSettings;
  commandCenter?: CommandCenterSettings;
  neuralNetwork?: NeuralNetworkSettings;
}

// Layout configuration interface
export interface LayoutConfig {
  type: FuturisticLayoutType;
  category: LayoutCategory;
  settings: LayoutSettings;
}

// Layout template interface (from database)
export interface LayoutTemplate {
  id: string;
  name: string;
  description?: string;
  thumbnailUrl?: string;
  layoutType: FuturisticLayoutType;
  category: LayoutCategory;
  defaultConfig: LayoutSettings;
  isDefault: boolean;
  version: string;
  createdAt: string;
  updatedAt: string;
}

// Layout metadata for UI display
export interface LayoutMetadata {
  type: FuturisticLayoutType;
  name: string;
  description: string;
  category: LayoutCategory;
  icon: string;
  previewImage?: string;
  isPremium: boolean;
}

// Default settings for each layout type
export const DEFAULT_LAYOUT_SETTINGS: Record<FuturisticLayoutType, Partial<LayoutSettings>> = {
  // Basic layouts
  SIDEBAR: { animationSpeed: 'normal', reducedMotion: false, darkMode: false },
  TOPBAR: { animationSpeed: 'normal', reducedMotion: false, darkMode: false },
  MINIMAL: { animationSpeed: 'normal', reducedMotion: false, darkMode: false },
  DASHBOARD_FIRST: { animationSpeed: 'normal', reducedMotion: false, darkMode: false },
  
  // Navigation Innovation
  BUBBLE_NAVIGATION: {
    animationSpeed: 'normal',
    reducedMotion: false,
    darkMode: true,
    bubbleNavigation: { arrangement: 'circular', bubbleSize: 56, showLabelsOnHover: true },
  },
  ORBITAL: {
    animationSpeed: 'normal',
    reducedMotion: false,
    darkMode: true,
    orbital: { orbitRadius: 200, orbitSpeed: 20, pauseOnFocus: true },
  },
  HEXAGONAL_GRID: {
    animationSpeed: 'normal',
    reducedMotion: false,
    darkMode: true,
    hexagonalGrid: { hexagonSize: 80, gap: 8, columns: 3 },
  },
  
  // Visual Effects
  GLASS_MORPHISM: {
    animationSpeed: 'normal',
    reducedMotion: false,
    darkMode: true,
    glassMorphism: { blurIntensity: 'medium', transparency: 0.1, borderGlow: true },
  },
  NEON_CYBERPUNK: {
    animationSpeed: 'normal',
    reducedMotion: false,
    darkMode: true,
    neonCyberpunk: { primaryNeonColor: 'cyan', secondaryNeonColor: 'magenta', neonIntensity: 'medium', flickerEnabled: false },
  },
  AURORA_BOREALIS: {
    animationSpeed: 'normal',
    reducedMotion: false,
    darkMode: true,
    auroraBorealis: { colors: ['#22d3ee', '#a855f7', '#3b82f6'], waveSpeed: 'normal' },
  },
  
  // 3D and Depth
  ISOMETRIC_3D: {
    animationSpeed: 'normal',
    reducedMotion: false,
    darkMode: true,
    isometric3D: { perspectiveAngle: 45, depth: 20, tiltOnHover: true },
  },
  PARALLAX_LAYERS: {
    animationSpeed: 'normal',
    reducedMotion: false,
    darkMode: true,
    parallaxLayers: { layerCount: 3, parallaxIntensity: 0.5 },
  },
  FLOATING_CARDS: {
    animationSpeed: 'normal',
    reducedMotion: false,
    darkMode: true,
    floatingCards: { elevationLevel: 3, shadowColor: 'rgba(0,0,0,0.3)', floatAnimation: true },
  },
  
  // Animations and Particles
  PARTICLE_FLOW: {
    animationSpeed: 'normal',
    reducedMotion: false,
    darkMode: true,
    particleFlow: { particleCount: 80, connectionDistance: 150, particleColor: 'rgba(34,211,238,0.8)', lineColor: 'rgba(34,211,238,0.2)', interactive: true },
  },
  LIQUID_MOTION: {
    animationSpeed: 'normal',
    reducedMotion: false,
    darkMode: true,
    liquidMotion: { blobCount: 4, blobColors: ['#22d3ee', '#a855f7', '#3b82f6', '#10b981'], morphSpeed: 'normal' },
  },
  MATRIX_RAIN: {
    animationSpeed: 'normal',
    reducedMotion: false,
    darkMode: true,
    matrixRain: { density: 0.05, speed: 50, characters: '0123456789ABCDEFアイウエオカキクケコ', rainColor: '#22d3ee' },
  },
  
  // Sci-Fi
  HOLOGRAPHIC: {
    animationSpeed: 'normal',
    reducedMotion: false,
    darkMode: true,
    holographic: { colorPalette: ['#22d3ee', '#a855f7', '#ec4899', '#3b82f6'], shimmerSpeed: 'normal', mouseInteraction: true },
  },
  COMMAND_CENTER: {
    animationSpeed: 'normal',
    reducedMotion: false,
    darkMode: true,
    commandCenter: { scanLinesEnabled: true, gridOverlay: true, accentColor: '#22d3ee', monospaceFont: true },
  },
  NEURAL_NETWORK: {
    animationSpeed: 'normal',
    reducedMotion: false,
    darkMode: true,
    neuralNetwork: { nodeCount: 20, connectionStyle: 'curved', pulseAnimation: true, dataFlowSpeed: 'normal' },
  },
};

// Layout category metadata
export const LAYOUT_CATEGORIES: Record<LayoutCategory, { name: string; description: string; icon: string }> = {
  BASIC: {
    name: 'Básico',
    description: 'Layouts tradicionais e funcionais',
    icon: 'Layout',
  },
  NAVIGATION: {
    name: 'Navegação Inovadora',
    description: 'Formas criativas de navegar pela aplicação',
    icon: 'Navigation',
  },
  VISUAL_EFFECTS: {
    name: 'Efeitos Visuais',
    description: 'Efeitos visuais modernos e elegantes',
    icon: 'Sparkles',
  },
  '3D_DEPTH': {
    name: '3D e Profundidade',
    description: 'Layouts com perspectiva e profundidade',
    icon: 'Box',
  },
  ANIMATIONS: {
    name: 'Animações',
    description: 'Backgrounds animados e partículas',
    icon: 'Zap',
  },
  SCIFI: {
    name: 'Sci-Fi',
    description: 'Estética futurista e tecnológica',
    icon: 'Cpu',
  },
};

// All layout metadata for UI
export const LAYOUT_METADATA: LayoutMetadata[] = [
  // Basic
  { type: 'SIDEBAR', name: 'Sidebar', description: 'Menu lateral clássico', category: 'BASIC', icon: 'PanelLeft', isPremium: false },
  { type: 'TOPBAR', name: 'Topbar', description: 'Menu superior horizontal', category: 'BASIC', icon: 'PanelTop', isPremium: false },
  { type: 'MINIMAL', name: 'Minimal', description: 'Design minimalista', category: 'BASIC', icon: 'Minus', isPremium: false },
  { type: 'DASHBOARD_FIRST', name: 'Dashboard First', description: 'Foco no dashboard', category: 'BASIC', icon: 'LayoutDashboard', isPremium: false },
  
  // Navigation Innovation
  { type: 'BUBBLE_NAVIGATION', name: 'Bubble Navigation', description: 'Navegação com bolhas flutuantes animadas', category: 'NAVIGATION', icon: 'Circle', isPremium: true },
  { type: 'ORBITAL', name: 'Orbital', description: 'Módulos orbitando ao redor do centro', category: 'NAVIGATION', icon: 'Orbit', isPremium: true },
  { type: 'HEXAGONAL_GRID', name: 'Hexagonal Grid', description: 'Grade hexagonal estilo colmeia', category: 'NAVIGATION', icon: 'Hexagon', isPremium: true },
  
  // Visual Effects
  { type: 'GLASS_MORPHISM', name: 'Glass Morphism', description: 'Efeito de vidro fosco elegante', category: 'VISUAL_EFFECTS', icon: 'Square', isPremium: true },
  { type: 'NEON_CYBERPUNK', name: 'Neon Cyberpunk', description: 'Tema escuro com elementos neon brilhantes', category: 'VISUAL_EFFECTS', icon: 'Lightbulb', isPremium: true },
  { type: 'AURORA_BOREALIS', name: 'Aurora Borealis', description: 'Gradientes animados como aurora boreal', category: 'VISUAL_EFFECTS', icon: 'Waves', isPremium: true },
  
  // 3D and Depth
  { type: 'ISOMETRIC_3D', name: '3D Isometric', description: 'Cards com perspectiva isométrica 3D', category: '3D_DEPTH', icon: 'Box', isPremium: true },
  { type: 'PARALLAX_LAYERS', name: 'Parallax Layers', description: 'Múltiplas camadas com efeito parallax', category: '3D_DEPTH', icon: 'Layers', isPremium: true },
  { type: 'FLOATING_CARDS', name: 'Floating Cards', description: 'Cards flutuantes com sombras dinâmicas', category: '3D_DEPTH', icon: 'CreditCard', isPremium: true },
  
  // Animations
  { type: 'PARTICLE_FLOW', name: 'Particle Flow', description: 'Partículas conectadas em movimento', category: 'ANIMATIONS', icon: 'Sparkles', isPremium: true },
  { type: 'LIQUID_MOTION', name: 'Liquid Motion', description: 'Blobs líquidos animados', category: 'ANIMATIONS', icon: 'Droplet', isPremium: true },
  { type: 'MATRIX_RAIN', name: 'Matrix Rain', description: 'Chuva de caracteres estilo Matrix', category: 'ANIMATIONS', icon: 'Binary', isPremium: true },
  
  // Sci-Fi
  { type: 'HOLOGRAPHIC', name: 'Holographic', description: 'Efeitos holográficos iridescentes', category: 'SCIFI', icon: 'Gem', isPremium: true },
  { type: 'COMMAND_CENTER', name: 'Command Center', description: 'Estética de centro de comando espacial', category: 'SCIFI', icon: 'Monitor', isPremium: true },
  { type: 'NEURAL_NETWORK', name: 'Neural Network', description: 'Visualização de rede neural conectada', category: 'SCIFI', icon: 'Brain', isPremium: true },
];

// Helper function to get layout metadata by type
export function getLayoutMetadata(type: FuturisticLayoutType): LayoutMetadata | undefined {
  return LAYOUT_METADATA.find(m => m.type === type);
}

// Helper function to get layouts by category
export function getLayoutsByCategory(category: LayoutCategory): LayoutMetadata[] {
  return LAYOUT_METADATA.filter(m => m.category === category);
}

// Helper function to check if layout is futuristic (not basic)
export function isFuturisticLayout(type: FuturisticLayoutType): boolean {
  return !['SIDEBAR', 'TOPBAR', 'MINIMAL', 'DASHBOARD_FIRST'].includes(type);
}

// Helper function to get default settings for a layout type
export function getDefaultSettings(type: FuturisticLayoutType): Partial<LayoutSettings> {
  return DEFAULT_LAYOUT_SETTINGS[type] || {};
}
